/**
 * useTranslation — Captures local audio and streams PCM16 to the backend
 * for real-time translation via OpenAI Realtime API.
 *
 * Used by BOTH host and guest:
 *   - Host: enables translation, AudioWorklet captures host mic → backend → OpenAI → guest
 *   - Guest: auto-starts when translation-started event fires, streams guest mic → backend → OpenAI → host
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { Socket } from 'socket.io-client';

interface UseTranslationProps {
  roomId: string;
  socketRef: React.MutableRefObject<Socket | null>;
  localStreamRef: React.MutableRefObject<MediaStream | null>;
  isHost: boolean;
  /** Controlled by the host toggle (host only). Guest ignores this — it's driven by socket events. */
  enabled: boolean;
  /** Language the host speaks (e.g. 'en' or 'es'). Defaults to 'en'. */
  hostLanguage?: string;
  /** Language the guest speaks (e.g. 'en' or 'es'). Defaults to 'es'. */
  guestLanguage?: string;
  /** Becomes true when the peer is connected and socket is live — triggers guest listener re-registration. */
  socketConnected?: boolean;
}

/** Convert ArrayBuffer to base64 string */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function useTranslation({
  roomId,
  socketRef,
  localStreamRef,
  isHost,
  enabled,
  hostLanguage = 'en',
  guestLanguage = 'es',
  socketConnected = false,
}: UseTranslationProps) {
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null); // fallback for old WebViews
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null); // track which stream is connected
  const isCapturingRef = useRef(false);
  const roleRef = useRef<'host' | 'guest'>(isHost ? 'host' : 'guest');

  const stopCapture = useCallback(() => {
    isCapturingRef.current = false;

    // Signal worklet to stop
    workletNodeRef.current?.port.postMessage({ type: 'stop' });

    sourceNodeRef.current?.disconnect();
    workletNodeRef.current?.disconnect();
    scriptProcessorRef.current?.disconnect();

    workletNodeRef.current = null;
    scriptProcessorRef.current = null;
    sourceNodeRef.current = null;
    streamRef.current = null;

    audioCtxRef.current?.close().catch((err) => console.warn('[Translation] AudioContext close error:', err));
    audioCtxRef.current = null;
    setIsTranslating(false);
  }, []);

  const startCapture = useCallback(async (stream: MediaStream) => {
    if (isCapturingRef.current) return;
    if (!socketRef.current) return;

    isCapturingRef.current = true;
    streamRef.current = stream;

    console.log(`[useTranslation] Starting capture — role=${roleRef.current} hostLang=${hostLanguage} guestLang=${guestLanguage}`);

    try {
      const audioCtx = new AudioContext({ sampleRate: 48000 });
      audioCtxRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      const role = roleRef.current;
      const socket = socketRef.current;

      // Try AudioWorklet first (modern browsers)
      if (typeof AudioWorkletNode !== 'undefined') {
        try {
          await audioCtx.audioWorklet.addModule('/worklets/pcm-capture-processor.js');

          const workletNode = new AudioWorkletNode(audioCtx, 'pcm-capture-processor');
          workletNodeRef.current = workletNode;

          workletNode.port.onmessage = (event) => {
            if (event.data?.type === 'pcm' && isCapturingRef.current) {
              const pcmBase64 = arrayBufferToBase64(event.data.buffer);
              console.log(`[LATENCY][useTranslation] emitting chunk: sender=${role} size=${pcmBase64.length} t=${Date.now()}`);
              socket.emit('translation-audio-input', { roomId, pcmBase64, sender: role });
            }
          };

          source.connect(workletNode);
          // Don't connect workletNode to destination — we only want to tap the audio
          setIsTranslating(true);
          return;
        } catch (workletErr) {
          console.warn('[Translation] AudioWorklet failed, falling back to ScriptProcessor:', workletErr);
          workletNodeRef.current = null;
        }
      }

      // ScriptProcessorNode fallback (deprecated but works in older WebViews)
      const bufferSize = 4096;
      const scriptProcessor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
      scriptProcessorRef.current = scriptProcessor;

      // Downsampling state for ScriptProcessor fallback
      const targetRate = 24000; // OpenAI Realtime API pcm16 requires 24kHz — NOT 16kHz
      const ratio = audioCtx.sampleRate / targetRate;
      let sampleAccumulator: number[] = [];
      const CHUNK_SAMPLES = 2400; // 100ms at 24kHz

      scriptProcessor.onaudioprocess = (event) => {
        if (!isCapturingRef.current) return;
        const inputData = event.inputBuffer.getChannelData(0);

        for (let i = 0; i < inputData.length; i += ratio) {
          const idx = Math.floor(i);
          const sample = inputData[idx];
          const clamped = Math.max(-1.0, Math.min(1.0, sample));
          const int16 = clamped < 0 ? Math.round(clamped * 32768) : Math.round(clamped * 32767);
          sampleAccumulator.push(int16);
        }

        while (sampleAccumulator.length >= CHUNK_SAMPLES) {
          const chunk = sampleAccumulator.splice(0, CHUNK_SAMPLES);
          const int16Array = new Int16Array(chunk);
          const pcmBase64 = arrayBufferToBase64(int16Array.buffer);
          console.log(`[LATENCY][useTranslation] emitting chunk (fallback): sender=${role} size=${pcmBase64.length} t=${Date.now()}`);
          socket.emit('translation-audio-input', { roomId, pcmBase64, sender: role });
        }
      };

      source.connect(scriptProcessor);
      scriptProcessor.connect(audioCtx.destination); // ScriptProcessor requires destination connection
      setIsTranslating(true);

    } catch (err: any) {
      console.error('[Translation] Failed to start audio capture:', err.message);
      isCapturingRef.current = false;
      setIsTranslating(false);
    }
  }, [roomId, socketRef]);

  // Watch for localStream changes (e.g. camera flip, device switch)
  // Reconnect the source node to the new stream without restarting the backend session
  useEffect(() => {
    const currentStream = localStreamRef.current;
    if (!isCapturingRef.current || !currentStream) return;
    if (streamRef.current === currentStream) return; // same stream, no change

    console.log('[Translation] Stream changed, reconnecting audio capture');

    // Only disconnect the source node — the worklet/scriptProcessor stays connected
    // and processing. We just swap out what feeds into it.
    sourceNodeRef.current?.disconnect();
    sourceNodeRef.current = null;

    // Reconnect to new stream using existing AudioContext
    const audioCtx = audioCtxRef.current;
    if (!audioCtx) return;

    try {
      const newSource = audioCtx.createMediaStreamSource(currentStream);
      sourceNodeRef.current = newSource;
      streamRef.current = currentStream;

      if (workletNodeRef.current) {
        newSource.connect(workletNodeRef.current);
      } else if (scriptProcessorRef.current) {
        newSource.connect(scriptProcessorRef.current);
      }
    } catch (err: any) {
      console.error('[Translation] Failed to reconnect to new stream:', err.message);
    }
  });

  // Host: start/stop translation based on `enabled` toggle
  useEffect(() => {
    if (!isHost) return;

    if (enabled && !isCapturingRef.current) {
      const stream = localStreamRef.current;
      if (stream) {
        // Tell backend to start both OpenAI sessions with language config
        console.log(`[useTranslation] Emitting start-translation: roomId=${roomId} hostLang=${hostLanguage} guestLang=${guestLanguage}`);
        socketRef.current?.emit('start-translation', { roomId, hostLanguage, guestLanguage });
        // Start capturing host audio
        startCapture(stream);
      }
    } else if (!enabled && isCapturingRef.current) {
      stopCapture();
      console.log(`[useTranslation] Emitting stop-translation: roomId=${roomId}`);
      socketRef.current?.emit('stop-translation', { roomId });
    }
  }, [enabled, isHost, roomId, localStreamRef, socketRef, startCapture, stopCapture, hostLanguage, guestLanguage]);

  // Guest: listen for translation-started / translation-stopped events
  useEffect(() => {
    if (isHost) return;
    const socket = socketRef.current;
    if (!socket) return;

    const handleTranslationStarted = (data: { roomId: string }) => {
      if (data.roomId !== roomId) return;
      console.log(`[useTranslation] Event received: translation-started (guest auto-start)`);
      const stream = localStreamRef.current;
      if (stream && !isCapturingRef.current) {
        startCapture(stream);
      }
    };

    const handleTranslationStopped = (data: { roomId: string }) => {
      if (data.roomId !== roomId) return;
      console.log(`[useTranslation] Event received: translation-stopped`);
      stopCapture();
    };

    socket.on('translation-started', handleTranslationStarted);
    socket.on('translation-stopped', handleTranslationStopped);

    return () => {
      socket.off('translation-started', handleTranslationStarted);
      socket.off('translation-stopped', handleTranslationStopped);
    };
  }, [isHost, roomId, localStreamRef, socketRef, startCapture, stopCapture, socketConnected]);

  // Listen for translation errors (both host and guest)
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleError = (data: { roomId: string; message: string }) => {
      if (data.roomId !== roomId) return;
      console.error(`[useTranslation] ❌ Error: ${data.message}`);
      setTranslationError(data.message);
      stopCapture(); // ensure local capture is stopped too
    };

    socket.on('translation-error', handleError);
    return () => { socket.off('translation-error', handleError); };
  }, [roomId, socketRef, stopCapture]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isCapturingRef.current) {
        stopCapture();
        // Only host emits stop-translation (guest stops silently)
        if (isHost) {
          socketRef.current?.emit('stop-translation', { roomId });
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearError = useCallback(() => setTranslationError(null), []);

  return { isTranslating, translationError, clearError };
}
